import type { VideoSet } from "@pax/api-contract/common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createBrowserPlayer } from "./browser-player";
import {
  boundedTime,
  initialPlaybackState,
  type PlaybackState,
  type PlayerOptions,
  type VideoDecoderPreference,
  type VideoPlayback,
} from "./playback";
import { browserVideoSource, preferredVideoSource } from "./select-video-source";
import { createSoftwarePlayer } from "./software-player";

export interface VideoPlaybackOptions extends Partial<PlayerOptions> {
  decoder: VideoDecoderPreference;
}

export type VideoPlaybackControls = ReturnType<typeof useVideoPlayback>;

/**
 * Plays `video` with the browser's decoder where it can, and with the software decoder where
 * `decoder` allows it. A new `video` object with the same sources keeps the same playback.
 */
export function useVideoPlayback(
  video: VideoSet,
  { decoder, loop = false, onFrame }: VideoPlaybackOptions,
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const playerRef = useRef<VideoPlayback | null>(null);
  const [state, setState] = useState(() => initialPlaybackState(video.durationSeconds));
  const stateRef = useRef<PlaybackState>(state);
  const [software, setSoftware] = useState(decoder === "software");
  const seekAnimation = useRef(0);
  const seekPending = useRef(false);
  const seekTarget = useRef(0);
  const videoKey = JSON.stringify(video);
  const stableVideo = useMemo(() => JSON.parse(videoKey) as VideoSet, [videoKey]);
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  const watchesFrames = onFrame !== undefined;

  useEffect(() => {
    const element = videoRef.current;
    const canvas = canvasRef.current;
    if (!element || !canvas) return;
    const { sources, durationSeconds, hasAudio } = stableVideo;
    const options: PlayerOptions = {
      loop,
      onFrame: watchesFrames ? mediaTime => onFrameRef.current?.(mediaTime) : undefined,
    };
    let disposed = false;
    let fellBack = false;
    let player: VideoPlayback | null = null;
    let observer: ResizeObserver | null = null;
    let source = decoder === "software" ? null : browserVideoSource(element, sources);
    const use = (next: VideoPlayback | null) => {
      player?.dispose();
      player = next;
      playerRef.current = next;
    };
    const report = (patch: Partial<PlaybackState>) => {
      if (disposed) return;
      if (
        Object.entries(patch).every(
          ([key, value]) => stateRef.current[key as keyof PlaybackState] === value,
        )
      )
        return;
      const next = { ...stateRef.current, ...patch };
      stateRef.current = next;
      setState(next);
    };
    report(initialPlaybackState(durationSeconds));
    setSoftware(false);
    const fallBack = () => {
      if (disposed || fellBack) return;
      fellBack = true;
      const previous = stateRef.current;
      use(null);
      if (decoder === "native") {
        report({ playing: false, waiting: false });
        return;
      }
      setSoftware(true);
      const next = createSoftwarePlayer(
        canvas,
        sources[0],
        { duration: durationSeconds, hasAudio, ...options },
        report,
      );
      use(next);
      next.setVolume(previous.volume);
      next.setMuted(previous.muted);
      if (previous.playing || previous.currentTime > 0) {
        next.seek(previous.currentTime);
        if (previous.playing)
          void next.play().catch(() => report({ playing: false, waiting: false }));
      }
    };
    if (source) {
      use(createBrowserPlayer(element, source.url, report, fallBack, options));
      const select = () => {
        void preferredVideoSource(element, sources).then(preferred => {
          if (disposed || fellBack || stateRef.current.playing || stateRef.current.currentTime > 0)
            return;
          if (!preferred) {
            fallBack();
            return;
          }
          if (preferred.url === source?.url) return;
          source = preferred;
          use(null);
          const next = createBrowserPlayer(element, preferred.url, report, fallBack, options);
          use(next);
          if (loop) return;
          next.setVolume(stateRef.current.volume);
          next.setMuted(stateRef.current.muted);
        });
      };
      observer = new ResizeObserver(select);
      observer.observe(element);
      select();
    } else fallBack();
    const pauseWhenHidden = () => {
      if (document.hidden) player?.pause();
    };
    document.addEventListener("visibilitychange", pauseWhenHidden);
    return () => {
      disposed = true;
      observer?.disconnect();
      document.removeEventListener("visibilitychange", pauseWhenHidden);
      cancelAnimationFrame(seekAnimation.current);
      seekAnimation.current = 0;
      seekPending.current = false;
      use(null);
    };
  }, [stableVideo, decoder, loop, watchesFrames]);

  const play = useCallback(() => {
    const next = { ...stateRef.current, started: true };
    stateRef.current = next;
    setState(next);
    if (seekPending.current) {
      cancelAnimationFrame(seekAnimation.current);
      seekAnimation.current = 0;
      seekPending.current = false;
      playerRef.current?.seek(seekTarget.current);
    }
    const player = playerRef.current;
    void player?.play().catch(() => {
      if (playerRef.current !== player) return;
      const next = {
        ...stateRef.current,
        playing: false,
        waiting: false,
        error: "The video could not be played. Try again.",
      };
      stateRef.current = next;
      setState(next);
    });
  }, []);
  const prepare = useCallback(() => playerRef.current?.prepare(), []);
  const pause = useCallback(() => playerRef.current?.pause(), []);
  const toggle = useCallback(() => {
    if (stateRef.current.playing) pause();
    else play();
  }, [play, pause]);
  const seek = useCallback((seconds: number) => {
    seekTarget.current = boundedTime(seconds, stateRef.current.duration);
    const next = {
      ...stateRef.current,
      started: true,
      currentTime: seekTarget.current,
      ended: false,
    };
    stateRef.current = next;
    setState(next);
    // The first seek goes out at once; while dragging, the newest follows once per frame.
    if (seekAnimation.current) {
      seekPending.current = true;
      return;
    }
    playerRef.current?.seek(seekTarget.current);
    const sendNewest = () => {
      seekAnimation.current = 0;
      if (!seekPending.current) return;
      seekPending.current = false;
      playerRef.current?.seek(seekTarget.current);
      seekAnimation.current = requestAnimationFrame(sendNewest);
    };
    seekAnimation.current = requestAnimationFrame(sendNewest);
  }, []);
  const setVolume = useCallback((volume: number) => {
    playerRef.current?.setVolume(volume);
    if (volume > 0) playerRef.current?.setMuted(false);
  }, []);
  const toggleMute = useCallback(() => playerRef.current?.setMuted(!stateRef.current.muted), []);
  const getCurrentTime = useCallback(
    () => playerRef.current?.getCurrentTime() ?? stateRef.current.currentTime,
    [],
  );

  return {
    state,
    software,
    videoRef,
    canvasRef,
    prepare,
    play,
    pause,
    toggle,
    seek,
    setVolume,
    toggleMute,
    getCurrentTime,
  };
}
